import React, { ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('✅ ERROR BOUNDARY CAUGHT THE ERROR:', error, errorInfo);
        console.log('🛡️ Error boundary is working! Close the dev overlay to see the fallback UI.');
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                    maxWidth: '600px',
                    margin: '100px auto'
                }}>
                    <h2 style={{ color: '#d32f2f', marginBottom: '20px' }}>
                        Something went wrong
                    </h2>
                    <p style={{ color: '#666', marginBottom: '30px' }}>
                        We're sorry, but something unexpected happened.
                        Please try reloading the page.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Reload Page
                    </button>
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <details style={{
                            marginTop: '30px',
                            textAlign: 'left',
                            backgroundColor: '#f5f5f5',
                            padding: '15px',
                            borderRadius: '4px'
                        }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                Error Details (Development Only)
                            </summary>
                            <pre style={{
                                marginTop: '10px',
                                fontSize: '12px',
                                overflow: 'auto'
                            }}>
                                {this.state.error.toString()}
                                {'\n\n'}
                                {this.state.error.stack}
                            </pre>
                        </details>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}
